import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import _, { has } from 'lodash';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import Icon from '@mdi/react';
import { mdiTableRefresh, mdiSyncAlert } from '@mdi/js';
import {
    Button,
    Accordion,
    AccordionSummary,
    AccordionActions,
    Divider,
    Avatar,
    Typography,
    IconButton,
    Tooltip,
    Checkbox,
} from '@material-ui/core';
import {
    UPDATE_DRAFT_SCHEDULE_SELECTION,
    UPDATE_DRAFT_SCHEDULE_SELECTION_SELECTED_WORKPLAN,
    importExcelFileDraft,
    downloadExcelTemplateFileDraft,
    saveScheduleTemplateSelectionsDraft,
    retrieveWorkplansDraft,
    UPDATE_DRAFT_BID_PROFILE,
    updateRetrieveImportDraftScheduleCompleted,
} from '../../store/bidProfileDraft';
import {
    UPDATE_SCHEDULE_SELECTION,
    UPDATE_SCHEDULE_SELECTION_SELECTED_WORKPLAN,
    saveScheduleTemplateSelections,
    importExcelFile,
    downloadExcelTemplateFile,
    retrieveWorkplans,
    UPDATE_BID_PROFILE,
    updateRetrieveImportScheduleCompleted,
} from '../../store/bidProfile';
import { loadWorkplans } from '../../store/appData';
import { UploadingFileSpinner } from '../../../components';
import {
    ScheduleSource, BidProfileStatus, WorkplanStatus, ImportWorkplanSteps,
} from '../../../Enums';
import './ScheduleSelection.scss';
import ExcelSelection from './ExcelSelection';
import WorkplanSelection from './WorkplanSelection';
import ShiftlineTable from './ShiftlineTable';
import { isWorkplanShiftlinesModified, getIsAllowBidOnActiveWorkplan } from './ScheduleSelection.service';
import { getDraftImportScheduleProgressStatusQuery, getImportScheduleProgressStatusQuery } from '../../services/api-endpoints';

function ScheduleSelection() {
    const dispatch = useDispatch();
    const { t } = useTranslation('shiftBidding');

    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const {
        managerAppData, bidProfileDraft, bidProfile, pendingTasks,
    } = useSelector((state) => state);
    const {
        bidProfileDrafts, bidProfiles, workplans, managerBidSetups,
    } = managerAppData;

    const [workplanSelections, setWorkplanSelections] = useState(workplans);
    const [uploadingCsv, setUploadingCsv] = useState(false);
    /**
     * @type {[number|undefined, Function]}
     */
    const [importingProgressValue, setImportingProgressValue] = useState(undefined);
    /**
     * @type {[ImportScheduleSteps, Function]}
     */
    const [importingProgressStepCode, setImportingProgressStepCode] = useState(ImportWorkplanSteps.Loading);

    /**
     * @param {ManagerAppData.IWorkplanBrief} workplan
     * @param {ManagerAppData.IWorkplanBrief | undefined} selectedWorkplan
     */
    const setWorkplanListItemDisabled = (workplan, selectedWorkplan) => {
        workplan.disabled = false;
        if (selectedWorkplan) {
            workplan.disabled = (workplan.type !== selectedWorkplan.type
                || workplan.length !== selectedWorkplan.length
                || workplan.anchorDay !== selectedWorkplan.anchorDay
            ) || (selectedWorkplan.workplanStatus === WorkplanStatus.Played && workplan.id !== selectedWorkplan.id);
        }
    };

    /**
     * @param {ManagerAppData.IWorkplanBrief[]} selections
     * @param {(string | number)[] } selectedWorkplans
     * @returns {ManagerAppData.IWorkplanBrief | undefined}
     */
    const findFirstSelectedWorkplans = (selections, selectedWorkplans) => {
        if (selectedWorkplans?.length) {
            return selections.find((x) => x.id === selectedWorkplans[0]);
        }
        return undefined;
    };

    /**
     * @param {boolean} expanded
     */
    // eslint-disable-next-line no-unused-vars
    let toggleAccordion = (expanded) => { };

    /**
     * @param {import('@commonClient2/../typings/transferList').TransferListItem[]} selections
     */
    // eslint-disable-next-line no-unused-vars
    let handleScheduleTemplateListChange = (selections) => { };
    let handleRetrieveSchedulePatternsClick = () => { };

    // @ts-ignore
    // eslint-disable-next-line no-unused-vars
    let handleUploadExcelFile = (file) => { };
    let handleConfirm = () => { };
    let handleDiscard = () => { };
    let handleDownloadExcelTemplate = () => { };
    let handleGetImportProgressStatus = () => { };
    let handleImportScheduleCompleted = () => { };
    // eslint-disable-next-line no-unused-vars
    let updateIsActiveWorkplanBid = (checked) => { };
    let isReImportNeeded = false;
    let isSelectedItemsUnchanged = false;
    let isUploadingSchedule = false;

    /**
     * @type {BiddingDraft.IBidProfileDraft | BidProfile.IBidProfile}
     */
    let profile;
    if (bidProfileDraft.id !== '') {
        profile = bidProfileDraft;
        const { scheduleSource } = profile.originalBidProfileInformation;
        isReImportNeeded = scheduleSource === ScheduleSource.WorkplanManager
            && profile.originalScheduleTemplates.isReImportNeeded;
        isUploadingSchedule = bidProfileDraft.isUploadingSchedule;

        handleConfirm = () => {
            dispatch(saveScheduleTemplateSelectionsDraft(
                bidProfileDraft.draftId,
                bidProfileDraft.bidProfileInformation.scheduleSource,
                { bidProfileDrafts, scheduleTemplates: profile.scheduleTemplates },
            ));
        };

        handleDownloadExcelTemplate = () => {
            dispatch(downloadExcelTemplateFileDraft(bidProfileDraft.draftId, bidProfileDraft.originalBidProfileInformation.name));
        };

        updateIsActiveWorkplanBid = (checked) => {
            dispatch({
                type: UPDATE_DRAFT_SCHEDULE_SELECTION,
                payload: { isActiveWorkplanBid: checked },
            });
        };

        handleUploadExcelFile = async (file) => {
            setUploadingCsv(true);
            await dispatch(importExcelFileDraft(bidProfileDraft.draftId, file));
            setUploadingCsv(false);
        };

        handleGetImportProgressStatus = () => getDraftImportScheduleProgressStatusQuery(bidProfileDraft.draftId);

        handleImportScheduleCompleted = () => {
            dispatch(updateRetrieveImportDraftScheduleCompleted(bidProfileDraft.draftId, { bidProfileDrafts }));
        };

        handleDiscard = () => {
            const newWorkplanSelections = _.cloneDeep(workplans);

            const selectedWorkplan = findFirstSelectedWorkplans(newWorkplanSelections, bidProfileDraft.originalScheduleTemplates.selected);

            for (const workplan of newWorkplanSelections) {
                workplan.selected = !!bidProfileDraft.originalScheduleTemplates.selected.includes(workplan.id);
                workplan.checked = false;
                setWorkplanListItemDisabled(workplan, selectedWorkplan);
            }

            setWorkplanSelections(newWorkplanSelections);
            dispatch({
                type: UPDATE_DRAFT_BID_PROFILE,
                payload: { isWorkPlansRetrieved: false },
            });
            dispatch({
                type: UPDATE_DRAFT_SCHEDULE_SELECTION,
                payload: _.cloneDeep({ ...bidProfileDraft.originalScheduleTemplates, isExpanded: true }),
            });
        };

        handleScheduleTemplateListChange = (selections) => {
            const newWorkplanSelections = _.cloneDeep(workplanSelections);
            const selectedIds = selections.filter((x) => x.selected)
                .map((x) => x.id.toString());

            const selectedWorkplan = findFirstSelectedWorkplans(newWorkplanSelections, selectedIds);

            for (const workplan of newWorkplanSelections) {
                // @ts-ignore
                workplan.selected = selectedIds.includes(workplan.id);
                workplan.checked = false;
                setWorkplanListItemDisabled(workplan, selectedWorkplan);
            }

            setWorkplanSelections(newWorkplanSelections);
            dispatch({
                type: UPDATE_DRAFT_BID_PROFILE,
                payload: { isWorkPlansRetrieved: false },
            });
            dispatch({
                type: UPDATE_DRAFT_SCHEDULE_SELECTION_SELECTED_WORKPLAN,
                payload: selectedIds,
            });
        };

        handleRetrieveSchedulePatternsClick = () => {
            dispatch(retrieveWorkplansDraft(bidProfileDraft.draftId, profile.scheduleTemplates.selected, false));
        };

        toggleAccordion = (expanded) => {
            dispatch({
                type: UPDATE_DRAFT_SCHEDULE_SELECTION,
                payload: { isExpanded: expanded },
            });
        };
    } else if (bidProfile.id !== '') {
        profile = bidProfile;
        isUploadingSchedule = bidProfile.isUploadingSchedule;

        handleConfirm = () => {
            dispatch(saveScheduleTemplateSelections(
                bidProfile.profileId,
                bidProfile.bidProfileInformation.scheduleSource,
                {
                    bidProfiles,
                    scheduleTemplates: bidProfile.scheduleTemplates,
                },
            ));
        };

        handleDownloadExcelTemplate = () => {
            dispatch(downloadExcelTemplateFile(bidProfile.profileId, bidProfile.originalBidProfileInformation.name));
        };

        updateIsActiveWorkplanBid = (checked) => {
            dispatch({
                type: UPDATE_SCHEDULE_SELECTION,
                payload: { isActiveWorkplanBid: checked },
            });
        };

        handleUploadExcelFile = async (file) => {
            setUploadingCsv(true);
            await dispatch(importExcelFile(bidProfile.profileId, file));
            setUploadingCsv(false);
        };

        handleGetImportProgressStatus = () => getImportScheduleProgressStatusQuery(bidProfile.profileId);

        handleImportScheduleCompleted = () => {
            dispatch(updateRetrieveImportScheduleCompleted(bidProfile.profileId, { bidProfiles }));
        };

        handleDiscard = () => {
            const newWorkplanSelections = _.cloneDeep(workplans);

            const selectedWorkplan = findFirstSelectedWorkplans(newWorkplanSelections, bidProfile.originalScheduleTemplates.selected);

            for (const workplan of newWorkplanSelections) {
                workplan.selected = !!bidProfile.originalScheduleTemplates.selected.includes(workplan.id);
                workplan.checked = false;
                setWorkplanListItemDisabled(workplan, selectedWorkplan);
            }

            setWorkplanSelections(newWorkplanSelections);
            dispatch({
                type: UPDATE_BID_PROFILE,
                payload: { isWorkPlansRetrieved: false },
            });
            dispatch({
                type: UPDATE_SCHEDULE_SELECTION,
                payload: _.cloneDeep({ ...bidProfile.originalScheduleTemplates, isExpanded: true }),
            });
        };

        handleScheduleTemplateListChange = (selections) => {
            const newWorkplanSelections = _.cloneDeep(workplanSelections);
            const selectedIds = selections.filter((x) => x.selected).map((x) => x.id.toString());

            const selectedWorkplan = findFirstSelectedWorkplans(newWorkplanSelections, selectedIds);

            for (const workplan of newWorkplanSelections) {
                // @ts-ignore
                workplan.selected = selectedIds.includes(workplan.id);
                workplan.checked = false;
                setWorkplanListItemDisabled(workplan, selectedWorkplan);
            }

            setWorkplanSelections(newWorkplanSelections);
            dispatch({
                type: UPDATE_BID_PROFILE,
                payload: { isWorkPlansRetrieved: false },
            });
            dispatch({
                type: UPDATE_SCHEDULE_SELECTION_SELECTED_WORKPLAN,
                payload: selectedIds,
            });
        };

        handleRetrieveSchedulePatternsClick = () => {
            dispatch(retrieveWorkplans(bidProfile.profileId, profile.scheduleTemplates.selected));
        };

        toggleAccordion = (expanded) => {
            dispatch({
                type: UPDATE_SCHEDULE_SELECTION,
                payload: { isExpanded: expanded },
            });
        };
    }

    const [isShowActiveWorkplanOnly, setIsShowActiveWorkplanOnly] = useState(profile.originalScheduleTemplates.isActiveWorkplanBid);

    /**
     * @param {ManagerAppData.IWorkplanBrief} _changedItem
     */
    const handleItemToggle = (_changedItem) => {
        /** @type {ManagerAppData.IWorkplanBrief | undefined} */
        let selectedWorkplan = workplanSelections.find((workplan) => workplan.selected);
        if (!selectedWorkplan) {
            selectedWorkplan = workplanSelections.find((workplan) => workplan.checked);
        }

        for (const workplan of workplanSelections) {
            setWorkplanListItemDisabled(workplan, selectedWorkplan);
        }

        setWorkplanSelections(workplanSelections);
    };

    useEffect(() => {
        const initWorkplanSelections = _.cloneDeep(workplans);
        const selectedWorkplan = findFirstSelectedWorkplans(initWorkplanSelections, profile.originalScheduleTemplates.selected);

        for (const workplan of initWorkplanSelections) {
            workplan.selected = !!profile.originalScheduleTemplates.selected?.includes(workplan.id);
            setWorkplanListItemDisabled(workplan, selectedWorkplan);
        }

        setWorkplanSelections(initWorkplanSelections);
    }, [JSON.stringify(workplans)]);

    useEffect(() => {
        if (isUploadingSchedule) {
            const interval = setInterval(async () => {
                const result = await handleGetImportProgressStatus();
                if (result.status === 200) {
                    const { progress, stepCode, isComplete } = result.data;
                    /**
                     * @type {number | undefined}
                     */
                    let progressValue = progress;
                    if (isComplete) {
                        // @ts-ignore
                        handleImportScheduleCompleted();
                        setImportingProgressStepCode(ImportWorkplanSteps.Loading);
                        setImportingProgressValue(undefined);
                        return;
                    }
                    if (stepCode === ImportWorkplanSteps.WrappingUp) {
                        progressValue = undefined;
                    }
                    setImportingProgressStepCode(stepCode);
                    setImportingProgressValue(progressValue);
                }
            }, 2000);
            return () => clearInterval(interval);
        }
    }, [isUploadingSchedule]);

    // @ts-ignore
    if (profile === undefined) {
        return <></>;
    }
    
    const {
        id,
        status,
        scheduleTemplates,
        originalScheduleTemplates,
        originalBidProfileInformation,
        isRebid,
        isCreationAllowed,
        isViewAllowed,
    } = profile;

    const { scheduleSource } = originalBidProfileInformation;
    
    const hasOnlyViewAccess = isViewAllowed && !isCreationAllowed;
    const isAnyItemsUploaded = scheduleTemplates?.items?.length > 0;
    const isAnyWorkplanSelected = scheduleTemplates?.selected?.length > 0;
    const isRefreshWorkPlanDisabled = [
        BidProfileStatus.Scheduled, BidProfileStatus.Active, BidProfileStatus.Closed,
        BidProfileStatus.Published, BidProfileStatus.Canceled]
        .includes(status);
    const isStatusAllowedUpdate = [BidProfileStatus.Draft, BidProfileStatus.Scheduled].includes(status);

    let selectionActions;
    let workplanSelection;

    switch (scheduleSource) {
        case ScheduleSource.WorkplanManager: {
            if (!isRebid) {
                workplanSelection = (
                    <WorkplanSelection
                        handleChange={handleScheduleTemplateListChange}
                        handleRetrieveSchedulePatternsClick={handleRetrieveSchedulePatternsClick}
                        items={workplanSelections}
                        isRetrieveButtonDisabled={!isAnyWorkplanSelected || !!pendingTasks || hasOnlyViewAccess}
                        workplanSelectionDisabled={isRefreshWorkPlanDisabled || hasOnlyViewAccess}
                        handleItemToggle={handleItemToggle}
                        isUploadingSchedule={isUploadingSchedule}
                        uploadScheduleProgressValue={importingProgressValue}
                        uploadScheduleStepCode={importingProgressStepCode}
                        hasOnlyViewAccess={hasOnlyViewAccess}
                    />
                );
                const isAllowBidOnActiveWorkplan = getIsAllowBidOnActiveWorkplan(
                    managerBidSetups,
                    originalBidProfileInformation.bidSetupId,
                );
                const onCheckShowActiveWorkplanOnly = (e) => {
                    const { checked } = e.target;
                    setIsShowActiveWorkplanOnly(checked);
                    updateIsActiveWorkplanBid(checked);
                    dispatch(loadWorkplans(id, checked));
                };
                selectionActions = hasOnlyViewAccess 
                    ? (<></>) 
                    : (
                        <div className="workplan-selection-actions">
                            {isAllowBidOnActiveWorkplan && (
                                <Tooltip
                                    title={t('form.scheduleSelection.workplan.showActiveWorkplanOnlyDisclaimer')}
                                    style={{ display: 'inline-flex', alignItems: 'center', paddingRight: '20px' }}
                                >
                                    <div className="workplan-selection-actions-checkbox">
                                        <Checkbox
                                            color="primary"
                                            checked={isShowActiveWorkplanOnly}
                                            onChange={onCheckShowActiveWorkplanOnly}
                                            disabled={!!pendingTasks || hasOnlyViewAccess}
                                        />
                                        <Typography>{t('form.scheduleSelection.workplan.showActiveWorkplanOnlyLabel')}</Typography>
                                    </div>
                                </Tooltip>
                            )}
                            <Tooltip
                                title="Refresh workplans"
                            >
                                <IconButton
                                    disabled={isRefreshWorkPlanDisabled || !!pendingTasks || hasOnlyViewAccess}
                                    className="bdb-recalculate-button"
                                    onClick={() => dispatch(loadWorkplans(id, isShowActiveWorkplanOnly))}
                                >
                                    <Icon
                                        path={mdiTableRefresh}
                                        size={1}
                                    />
                                </IconButton>
                            </Tooltip>
                        </div>
                    );
            }
            isSelectedItemsUnchanged = !isWorkplanShiftlinesModified(
                originalScheduleTemplates.retrievedWorkplans?.workplans,
                scheduleTemplates.retrievedWorkplans?.workplans,
            );
            isReImportNeeded = isReImportNeeded && isSelectedItemsUnchanged;

            break;
        }
        case ScheduleSource.ExcelFile:
            if (!isRebid) {
                selectionActions = (
                    <ExcelSelection
                        handleDownloadExcelTemplate={handleDownloadExcelTemplate}
                        handleUploadExcelFile={handleUploadExcelFile}
                        isUploadingSchedule={isUploadingSchedule}
                        uploadScheduleProgressValue={importingProgressValue}
                        uploadScheduleStepCode={importingProgressStepCode}
                        hasOnlyViewAccess={hasOnlyViewAccess}
                        disabled={!isStatusAllowedUpdate}
                    />
                );
            }
            break;
        default:
            selectionActions = null;
            break;
    }

    const isInputDisabled = !scheduleTemplates.isEditable || hasOnlyViewAccess;
    const isConfirmButtonDisabled = isReImportNeeded || !isAnyItemsUploaded || isUploadingSchedule || !isStatusAllowedUpdate;

    const handleReImportWorkplan = () => {
        dispatch(retrieveWorkplansDraft(id, scheduleTemplates.selected, true));
    };
    
    return (
        <Accordion
            expanded={scheduleTemplates.isExpanded}
            // @ts-ignore
            onChange={(event, expanded) => toggleAccordion(expanded)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
            >
                <Avatar className="bde-avatar">2</Avatar>
                <Typography variant="h6">{t('form.scheduleSelection.header')}</Typography>
            </AccordionSummary>
            <Divider />
            {selectionActions && (
                <div className="sub-header">
                    <div className="sub-header-buttons">
                        {selectionActions}
                    </div>
                </div>
            )}
            {workplanSelection}
            {uploadingCsv && (
                <div className="CsvUploadSpinnerContainer">
                    <UploadingFileSpinner />
                </div>
            )}
            <ShiftlineTable
                isSummary={false}
                isDisabled={isInputDisabled || isReImportNeeded}
            />
            {!hasOnlyViewAccess && (
                <>
                    <Divider />
                    <AccordionActions>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handleReImportWorkplan}
                            disabled={!isReImportNeeded || isUploadingSchedule}
                        >
                            <Tooltip
                                title={`The selected workplan template has been modified in Workplan Manager.
                                Refresh the selected templates to continue`}
                                arrow
                            >
                                <div>
                                    <Icon
                                        path={mdiSyncAlert}
                                        size={1.5}
                                        color={isReImportNeeded ? 'white' : 'rgba(0, 0, 0, 0.26)'}
                                    />
                                </div>
                            </Tooltip>
                        </Button>
                        <Button
                            color="secondary"
                            variant="contained"
                            onClick={handleDiscard}
                            disabled={isInputDisabled || isUploadingSchedule}
                        >
                            {t('globalForm.discard')}
                        </Button>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handleConfirm}
                            disabled={isConfirmButtonDisabled}
                            className="save-continue--button"
                        >
                            <FontAwesomeIcon icon={faCheckCircle} />
                            {t('globalForm.applyButton')}
                        </Button>
                    </AccordionActions>
                </>
            )}
        </Accordion>
    );
}

export default ScheduleSelection;
